// Mirrors config/analytics.php — the inline snippet in components/analytics.blade.php reads the
// same key before gtm.js loads.
export const CONSENT_STORAGE_KEY = 'tcw_cookie_consent';
export const CONSENT_ACCEPTED = 'accepted';
export const CONSENT_REJECTED = 'rejected';

export type ConsentChoice = typeof CONSENT_ACCEPTED | typeof CONSENT_REJECTED;
export type ConsentState = ConsentChoice | 'unset';

/**
 * Lives outside initCookieConsent() on purpose: the banner short-circuits once a choice exists,
 * so anything needing the current state must not depend on the banner being initialised.
 */
export function readConsent(): ConsentState {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);

    if (stored === CONSENT_ACCEPTED || stored === CONSENT_REJECTED) {
      return stored;
    }
  }
  catch {
    // Storage disabled (Safari private mode) — treat as no choice made.
  }

  return 'unset';
}

export function hasAnalyticsConsent(): boolean {
  return readConsent() === CONSENT_ACCEPTED;
}

/**
 * No-op when GTM was not rendered.
 *
 * ad_storage / ad_user_data / ad_personalization stay at their denied default and are deliberately
 * absent here: the banner asks about analytics and content personalisation, never about
 * advertising, so consent for ad profiling was never obtained. Adding them back requires the
 * banner copy to be extended first — see components/analytics.blade.php.
 */
export function syncConsentToGoogle(choice: ConsentChoice): void {
  const value = choice === CONSENT_ACCEPTED ? 'granted' : 'denied';

  window.gtag?.('consent', 'update', {
    analytics_storage: value,
    personalization_storage: value,
  });
}

export function persistConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  }
  catch {
    // Storage disabled — the consent update below still applies for this page view.
  }

  syncConsentToGoogle(choice);
}
